import { Box, Paginator, Spinner } from "@adfenix/alexandria";
import { useContext, useEffect } from "react";
import ErrorMessage from "components/ErrorMessage";
import {
  ReadLayerContext,
  ReadLayerData
} from "components/ReadLayer/ReadLayerQueryContext";
import { useRecoilValue } from "recoil";
import { useScreenClass } from "react-grid-system";
import { useTranslation } from "react-i18next";
import { userSelectedHq } from "atoms/auth";
import { PropertyListing } from "./PropertyListing";
import { PropertyService } from "@adfenix/types";

interface MessageInt {
  title: string;
  description: string;
}

type Title = "error" | "empty" | "noresults" | "none";

function getMessage(type: Title = "none"): MessageInt {
  const msgMap = {
    error: {
      title: "results.error.title",
      description: "results.error.description"
    },
    empty: {
      title: "listings.noproperties.title",
      description: "listings.noproperties.description"
    },
    noresults: {
      title: "listings.noresults.title",
      description: "listings.noresults.description"
    },
    none: {
      title: "",
      description: ""
    }
  };

  return msgMap[type];
}

export const PropertyListings = ({}) => {
  const ctx =
    useContext<ReadLayerData<PropertyService.PropertyWithCampaignsSearchModel> | null>(
      ReadLayerContext
    );
  const { t } = useTranslation();
  const screenClass = useScreenClass();
  const selectedHq = useRecoilValue(userSelectedHq);

  useEffect(() => {
    if (
      ctx &&
      selectedHq &&
      !ctx.query.filters.find(filter => filter.value === selectedHq.id)
    ) {
      const filter = [{ column: "hq.id", value: selectedHq?.id }];
      ctx.updateQuery({ filters: filter });
    }
  }, [ctx, selectedHq]);

  if (!ctx) return null;

  const properties = ctx.data?.documents;

  if (!properties || !ctx.updateQuery) {
    return (
      <Box pY="xl5" display="flex" justifyContent="center">
        <Spinner />
      </Box>
    );
  }

  const getErrorType = () => {
    if (!properties.length) {
      return ctx.query ? "noresults" : "empty";
    }
    return;
  };

  const hasError = getErrorType();

  if (hasError) {
    const { title: errorTitle, description: errorDescription } =
      getMessage(hasError);

    return (
      <ErrorMessage title={t(errorTitle)} description={t(errorDescription)} />
    );
  }

  return (
    <>
      {properties?.map(p => {
        return (
          <PropertyListing key={p.id} property={p} screenClass={screenClass} />
        );
      })}

      <Box display="flex" justifyContent="center">
        <Paginator
          onChange={page => ctx.updateQuery({ page })}
          numbersInMiddle={5}
          pageSize={ctx.query.pageSize ?? 10}
          numberOfItems={Math.min(
            ctx.data?.totalNumberOfDocuments ?? 10,
            10000
          )}
          activePage={ctx.query.page ?? 1}
        />
      </Box>
    </>
  );
};
